import { Dictionary } from 'lodash';
import { Currency } from 'api/account';
import {
  Role as RoleT,
  Team as TeamT,
  User as UserT,
  Timestamp,
  DateType,
} from 'api/models';
import { RateCardLite } from 'api/rate-cards';
import { FixedFeeRevenueRecognitionTypes } from 'api/settings-financials/types';
import { Sprint } from 'api/sprint-planning/types';
import { Task as TaskT } from 'api/tasks';
import { DateValue } from 'components/atoms/due-date-picker';
import {
  FinancialsTableGroupBy,
  FinancialsZoomLevel,
  FinancialsBudgetBase,
  FinancialsHoursBase,
} from 'components/organisms/financials/types/common';
import {
  Direction,
  ShiftBy,
} from 'components/organisms/tasks/bulk-editor/shift-dates/model';
import { Project as ProjectT, ProjectPhase as ProjectPhaseT } from '.';

export interface ProjectProgressPlots {
  series: {
    PLANNED: {
      [key: string]: number;
    };
    // eslint-disable-next-line @typescript-eslint/naming-convention
    IN_PROGRESS: {
      [key: string]: number;
    };
    COMPLETED: {
      [key: string]: number;
    };
  };
}

export interface AddTeamToProjectPayload {
  teamId: number;
}

export interface AddTeamToProjectResponse {
  teamId: number;
  identityType: 'TEAM';
}

export type RemoveTeamFromProjectPayload = AddTeamToProjectPayload;
export type RemoveTeamFromProjectResponse = AddTeamToProjectResponse;

export type ProjectUsersLiteQueryKey = ['project-users-lite', string];

export interface ProjectUserLite {
  user: Pick<UserT, 'userId'>;
  role?: RoleT;
  referenceTeam?: TeamT;
}

export interface ProjectUserLiteMembership {
  projectId: number;
  members: ProjectUserLite[];
}

export interface ProjectUserIdentifier {
  userId: number;
  roleId?: number;
  referenceTeamId?: number;
}

export enum EventSource {
  ResourceManagement = 'RESOURCE_MANAGEMENT',
  PlaceholderManage = 'PLACEHOLDER_MANAGE',
}

export interface AddProjectUserLiteProps {
  members: ProjectUserIdentifier[];
  source?: EventSource;
  assignedToProjectMember?: boolean;
}

export type RemoveProjectUserLiteProps = Omit<
  AddProjectUserLiteProps,
  'assignedToProjectMember'
>;

export type ProjectUserLiteMutationProps = {
  projectId: number;
  projectUsers: ProjectUserIdentifier[];
  source?: EventSource;
  assignedToProjectMember?: boolean;
};

export type ProjectBaseline = {
  baselineId: number;
  baselineName: string;
  baselineNote?: string;
  projectPhases: Pick<
    ProjectPhaseT,
    'projectPhaseId' | 'startDate' | 'dueDate'
  >[];
  tasks: Pick<TaskT, 'taskId' | 'startDate' | 'dueDate'>[];
  sprints: Pick<Sprint, 'sprintId' | 'startDate' | 'dueDate'>[];
  default: boolean;
  createdAt: Timestamp;
  /**
   * for soft deletion in FE
   * since Gantt doesnt have an API to clear baseline
   * all the baselines will be retained unless there is re-render (re-remder is prevented for perf)
   * and we need proper indices to toggle baseline, hence soft deletion.
   */
  deleted?: boolean;
};

export type ProjectBaselineCreateRequest = Omit<
  ProjectBaseline,
  'baselineId' | 'createdAt' | 'default'
>;

export type ProjectBaselineUpdateRequest = Pick<
  ProjectBaseline,
  'baselineName' | 'baselineId' | 'baselineNote'
>;

export type ProjectBaselineWriteResponse = ProjectBaseline;

export type ProjectBaselineGetResponse = ProjectBaseline[];

export enum ShiftDatesType {
  'PROJECTPLAN' = 'PROJECT_PLAN',
  'TASKS' = 'TASKS',
}

export enum ShiftDatesKind {
  'TO' = 'TO',
  'BY' = 'BY',
}

export interface BulkShiftDatesRequest {
  shiftType: ShiftDatesType;
  taskId?: number;
  taskStartDate?: string;
  phaseId?: number;
  phaseStartDate?: string;
  shiftValue: number;
  shiftUnit: ShiftBy;
  shiftCondition: Direction;
  projectBaseline?: any;
}

export enum ProjectFinancialsContractType {
  FIXED = 'FIXED_FEE',
  TM = 'TIME_AND_MATERIAL',
  SUBSCRIPTION = 'SUBSCRIPTION',
  NONBILLABLE = 'NON_BILLABLE',
}

export enum ProjectFinancialsSubscriptionFrequency {
  Monthly = 'MONTHLY',
  Quarterly = 'QUARTERLY',
  Yearly = 'YEARLY',
}

export enum ProjectFinancialsTableTypeForApi {
  Budget = 'BUDGET',
  Hours = 'HOURS',
  Both = 'BOTH',
}

export enum ProjectFinancialsSourceType {
  Role = 'ROLE',
  User = 'USER',
  Team = 'TEAM',
  Google = 'GOOGLE_CALENDAR',
  Outlook = 'OULOOK_CALENDAR',
  Meeting = 'MEETING',
  Task = 'TASK',
  Phase = 'PHASE',
  Project = 'PROJECT',
  Activity = 'ACTIVITY',
  Adhoc = 'ADHOC',
  NonPhaseTask = 'NON_PHASE_TASK',
}

export enum BillRateSourceType {
  USER = 'USER',
  TEAM = 'TEAM',
}

export interface ProjectCustomRates {
  projectFinancialsId: number;
  projectId: number;
  sourceType: BillRateSourceType;
  sourceId: number;
  rate: number;
  currency: Currency;
  createdBy?: number;
  updatedBy?: number;
  updatedAt?: string;
  replacedUserId?: number;
}

export interface ProjectFinancialsDetailsSubscriptionMeta {
  subscriptionFrequency: ProjectFinancialsSubscriptionFrequency;
  subscriptionStartDate: string;
  periodBudget: number;
  periodMinutes?: number;
  carryOverRemainingHours: boolean;
  unlimitedPeriods: boolean;
  numberOfPeriods?: number;
}

export interface MilestoneRevenueRecognitionConfigs {
  milestoneRevenueRecognitionConfigId: number;
  sourceType: string;
  sourceId: number;
  recognisedRevenue: number;
  recognisedPercentage: number;
  notes?: string;
}

export interface ManualRevenueRecognitionConfigs {
  manualRevenueRecognitionConfigId: number;
  recognitionDate: DateValue;
  recognisedRevenue: number;
  recognisedPercentage: number;
  notes?: string;
}

export interface ProjectFinancialsDetails {
  projectId: number;
  projectFinancialsId: number;
  currency?: Currency; // TODO: to check if this is optional
  financialContractType?: ProjectFinancialsContractType;
  rateCardId?: number;
  rateCardVersionId?: number;
  projectBudget?: number;
  projectHours?: number;
  projectMinutes?: number;
  projectCustomRates?: ProjectCustomRates[];
  projectFinancialsMetaData?: ProjectFinancialsDetailsSubscriptionMeta;
  rateCard?: RateCardLite;
  billRatesBackdated?: boolean;
  createdBy?: number;
  createdAt?: string;
  updatedBy?: number;
  updatedAt?: string;
  notes?: string;
  revenueRecognitionType?: FixedFeeRevenueRecognitionTypes;
  milestoneRevenueRecognitionConfigs?: MilestoneRevenueRecognitionConfigs[];
  manualRevenueRecognitionConfigs?: ManualRevenueRecognitionConfigs[];
}

export type ProjectFinancialsHoursBreakdown = Pick<
  FinancialsHoursBase,
  'timeTracked' | 'timeAllocated'
>;

export interface ProjectFinancialsDateRange {
  startDate?: DateType;
  endDate?: DateType;
}

export interface ProjectFinancialsBreakdown
  extends ProjectFinancialsDateRange,
    ProjectFinancialsHoursBreakdown {
  actualBreakdown?: FinancialsBudgetBase;
  estimatedBreakdown?: FinancialsBudgetBase;
}

export interface ProjectFinancialsTableRowBaseData {
  sourceType: ProjectFinancialsSourceType;
  sourceName: string;
  role?: Partial<RoleT>;
  user?: Partial<UserT>;
  referenceTeam?: TeamT;
  task?: Partial<TaskT>;
  projectPhase?: Partial<ProjectPhaseT>;
  project?: Partial<ProjectT>;
  breakdownDetails: ProjectFinancialsBreakdown[];
}

export interface ProjectFinancialsTableRowApiData
  extends ProjectFinancialsTableRowBaseData {
  childrenBreakdown?: ProjectFinancialsTableRowApiData[];
}

export interface ProjectFinancialsTableRowData
  extends Omit<ProjectFinancialsTableRowBaseData, 'breakdownDetails'> {
  breakdownDetails: Dictionary<ProjectFinancialsBreakdown>;
  hierarchy: string[];
  isAlias: boolean;
  childCount?: number;
}

export interface ProjectFinancialsTableApiData {
  groupBy: FinancialsTableGroupBy;
  zoomLevel: FinancialsZoomLevel;
  breakdown?: ProjectFinancialsTableRowApiData[];
}

export interface ProjectFinancialsBudgetChartRevenueData {
  estimatedRevenue?: number;
  actualRevenue?: number;
  estimatedCost?: number;
  actualCost?: number;
}

export interface ProjectFinancialsBudgetLineChartApiData
  extends ProjectFinancialsBudgetChartRevenueData {
  projectBudget?: number;
  estimatedProfit?: number;
  actualProfit?: number;
  estimatedMargin?: number;
  actualMargin?: number;
}

export interface ProjectFinancialsHoursChartTrackedData {
  timeAllocated?: number;
  billableTimeTracked?: number;
  nonBillableTimeTracked?: number;
}

export interface ProjectFinancialsHoursLineChartApiData
  extends ProjectFinancialsHoursChartTrackedData {
  projectHours?: number;
  projectMinutes?: number;
  timeTracked?: number;
}

export type ProjectFinancialsLineChartApiData = Pick<
  ProjectFinancialsDateRange,
  'startDate'
> &
  ProjectFinancialsBudgetLineChartApiData &
  ProjectFinancialsHoursLineChartApiData;

export interface ProjectFinancialsBudgetBarChartApiData
  extends ProjectFinancialsBudgetChartRevenueData {
  margin?: number;
  profit?: number;
}

export type ProjectFinancialsBarChartApiData = ProjectFinancialsDateRange &
  ProjectFinancialsBudgetBarChartApiData &
  ProjectFinancialsHoursChartTrackedData;

export interface ProjectFinancalsSubscriptionCurrentPeriodData {
  currentPeriodNumber: number;
  numberOfPeriods: number;
  periodRevenue: number;
  periodTotalTimeTracked: number;
}

export interface ProjectFinancialsSubscriptionPeriodData
  extends ProjectFinancialsBarChartApiData {
  periodId: number;
  periodNumber: number;
  periodBudget: number;
  periodMinutes: number;
  currentPeriod?: boolean;
  remainingDaysInPeriod?: number;
}

export interface ProjectFinancialsApiData
  extends FinancialsBudgetBase,
    FinancialsHoursBase {
  projectId: number;
  projectFinancialDetails: ProjectFinancialsDetails;
  startDate?: DateType;
  endDate?: DateType;
  estimatedRevenue?: number;
  estimatedMargin?: number;
  pendingApprovalHours?: number;
  totalProjectBudget?: number;
  totalProjectMinutes?: number;
  financialsData?: ProjectFinancialsTableApiData;
  barChartData?: ProjectFinancialsBarChartApiData[];
  lineChartData?: ProjectFinancialsLineChartApiData[];
  periodDetails?: ProjectFinancialsSubscriptionPeriodData[];
  milestoneRevenueRecognitionConfigs?: MilestoneRevenueRecognitionConfigs[];
  manualRevenueRecognitionConfigs?: ManualRevenueRecognitionConfigs[];
}

export type ProjectFinancialsApiDataWithQuery = Pick<
  ProjectFinancialsApiData,
  | 'projectId'
  | 'startDate'
  | 'endDate'
  | 'projectFinancialDetails'
  | 'financialsData'
>;

export interface ProjectFinancialsApiQueryParams {
  groupBy?: FinancialsTableGroupBy;
  zoomLevel?: FinancialsZoomLevel;
  tabType?: ProjectFinancialsTableTypeForApi;
}

export interface ProjectFinancialsTableQueryData {
  projectId: number;
  queryParams?: ProjectFinancialsApiQueryParams;
}

export type ProjectFinancialsFieldNames =
  | 'costRate'
  | 'projectBudget'
  | 'projectHours'
  | 'projectMinutes'
  | 'projectRevenue'
  | 'projectProfitability'
  | 'projectCustomBillRate'
  | 'projectRateCardUpdate';

export type FinancialsTooltipKeys =
  | 'revenue'
  | 'cost'
  | 'profit'
  | 'margin'
  | 'estimatedRevenue'
  | 'estimatedCost'
  | 'estimatedProfit'
  | 'estimatedMargin'
  | 'projectBudget';

export interface ShiftTasksPayload {
  updatedPhases: ProjectPhaseT[];
  updatedTasks: TaskT[];
  useEntityOrder?: boolean;
}

export interface DependenciesMap {
  taskMap: {
    [key: number]: number[];
  };
  phaseMap: {
    [key: number]: number[];
  };
  taskFamily: {
    [key: number]: number[];
  };
}
